import {
  addToPosition,
  CurrencyMismatchError,
  fromDecimal,
  InvalidPositionError,
  isDecimalString,
  planSale,
  toDecimal,
  type Holding,
  type Position,
  type SalePlan,
} from '@porttang/core';
import { useMemo, useState } from 'react';
import { formatQuantity } from '../../shared/format';
import { SaleRealizedRow } from '../realized/SaleRealizedRow';

export type TradeMode = 'buy' | 'sell';

/** ผลที่คำนวณเสร็จแล้ว ผู้เรียกมีหน้าที่แค่เขียนลง Firestore */
export type TradeResult = { mode: 'buy'; next: Position } | { mode: 'sell'; plan: SalePlan };

const inputClass =
  'w-full rounded-md border border-line-strong bg-sunken px-3 py-2 text-ink-100 outline-none focus:border-brand';

/**
 * ผลของการคำนวณล่วงหน้า
 *
 * `idle` = ยังกรอกไม่ครบ ไม่ใช่ความผิดพลาด จึงไม่ขึ้นข้อความแดง
 */
type Preview =
  { state: 'idle' } | { state: 'error'; message: string } | { state: 'ok'; result: TradeResult };

/** ต้นทุนรวม = จำนวน × ต้นทุนต่อหน่วย — ใช้ decimal ตลอด ไม่แปลงเป็น number ระหว่างทาง */
function totalCost(position: Position): string {
  return fromDecimal(
    toDecimal(position.quantity, 'position.quantity').mul(
      toDecimal(position.avgCost, 'position.avgCost'),
    ),
  );
}

function formatAmount(decimalString: string, currency: string): string {
  const value = Number(decimalString);
  if (!Number.isFinite(value)) {
    return `${decimalString} ${currency}`;
  }
  return `${value.toLocaleString('th-TH', { maximumFractionDigits: 8 })} ${currency}`;
}

export function TradeForm({
  holding,
  mode,
  isSaving,
  onCancel,
  onSubmit,
}: {
  holding: Holding;
  mode: TradeMode;
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (result: TradeResult) => void;
}) {
  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');

  const current: Position = {
    quantity: holding.quantity,
    avgCost: holding.avgCost,
    currency: holding.currency,
  };

  const preview = useMemo<Preview>(() => {
    const qty = quantity.trim();
    const unitPrice = price.trim();

    // ขายก็ต้องมีราคา — ไม่มีราคาขายก็คำนวณกำไรที่รับรู้ไม่ได้ (spec Section 5.5)
    if (qty === '' || unitPrice === '') {
      return { state: 'idle' };
    }
    if (!isDecimalString(qty)) {
      return { state: 'error', message: 'จำนวนต้องเป็นตัวเลข' };
    }
    if (!isDecimalString(unitPrice)) {
      return { state: 'error', message: 'ราคาต้องเป็นตัวเลข' };
    }

    // ล็อกสกุลเงินไว้ที่ต้นทุนเดิมเสมอ ไม่เปิดให้เลือก — spec Section 5.3 ห้ามแปลงค่าเงินอัตโนมัติ
    // และวิธีที่กันพลาดได้จริงคือไม่ให้มีช่องให้กรอกผิดตั้งแต่แรก
    try {
      if (mode === 'buy') {
        const position: Position = {
          quantity: holding.quantity,
          avgCost: holding.avgCost,
          currency: holding.currency,
        };
        return {
          state: 'ok',
          result: {
            mode: 'buy',
            next: addToPosition(position, {
              quantity: qty,
              price: unitPrice,
              currency: holding.currency,
            }),
          },
        };
      }
      return {
        state: 'ok',
        result: {
          mode: 'sell',
          plan: planSale(holding, { quantity: qty, price: unitPrice, currency: holding.currency }),
        },
      };
    } catch (caught) {
      if (caught instanceof InvalidPositionError || caught instanceof CurrencyMismatchError) {
        return { state: 'error', message: caught.message };
      }
      throw caught;
    }
  }, [quantity, price, mode, holding]);

  const result = preview.state === 'ok' ? preview.result : null;
  const next = result === null ? undefined : result.mode === 'buy' ? result.next : result.plan.next;
  const isSellingOut = next === null;

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (result === null) {
      return;
    }
    onSubmit(result);
  }

  const title = mode === 'buy' ? `ซื้อเพิ่ม ${holding.symbol}` : `ขาย ${holding.symbol}`;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4">
      <form
        onSubmit={handleSubmit}
        className="my-8 w-full max-w-md rounded-[14px] border border-line bg-ink p-6"
      >
        <h2 className="text-lg font-semibold text-ink-100">{title}</h2>
        <p className="mt-1 text-xs text-ink-500">
          {holding.displayName} · {holding.platform}
        </p>

        <div className="mt-5 space-y-4">
          <label className="block">
            <span className="mb-1 block text-sm text-ink-200">
              {mode === 'buy' ? 'จำนวนที่ซื้อเพิ่ม' : 'จำนวนที่ขาย'}
            </span>
            <input
              className={inputClass}
              inputMode="decimal"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              autoFocus
            />
            <span className="mt-1 block text-xs text-ink-500">
              ตอนนี้ถืออยู่ {formatQuantity(holding.quantity)} หน่วย
            </span>
          </label>

          <label className="block">
            <span className="mb-1 block text-sm text-ink-200">
              {mode === 'buy' ? 'ราคาที่ซื้อ ต่อหน่วย' : 'ราคาที่ขาย ต่อหน่วย'}
            </span>
            <div className="flex items-center gap-2">
              <input
                className={inputClass}
                inputMode="decimal"
                value={price}
                onChange={(event) => setPrice(event.target.value)}
              />
              {/* สกุลเงินตายตัวตามต้นทุนเดิม — ไม่ใช่ช่องให้เลือก */}
              <span className="shrink-0 font-mono text-sm text-ink-400">{holding.currency}</span>
            </div>
            <span className="mt-1 block text-xs text-ink-500">
              ต้องเป็นราคาในสกุล {holding.currency} เท่านั้น
              {mode === 'buy'
                ? ' ถ้าซื้อมาด้วยสกุลอื่นให้แปลงก่อนกรอก'
                : ' ใช้คำนวณกำไรที่ขายได้จริง'}
            </span>
          </label>
        </div>

        {preview.state === 'error' && (
          <p
            role="alert"
            className="mt-5 rounded-lg border border-down/25 bg-down/10 px-3.5 py-2.5 text-xs leading-relaxed text-down"
          >
            {preview.message}
          </p>
        )}

        {result !== null && next !== undefined && (
          <div className="mt-5 rounded-lg border border-line-strong bg-sunken p-4">
            <div className="pixel-label">ก่อน → หลัง</div>

            {next === null ? (
              <p className="mt-3 text-sm leading-relaxed text-warn">
                ขายหมดพอดี — {holding.symbol} จะถูกลบออกจากพอร์ต
              </p>
            ) : (
              <dl className="mt-3 space-y-2.5 text-sm">
                <PreviewRow
                  label="จำนวน"
                  before={formatQuantity(holding.quantity)}
                  after={formatQuantity(next.quantity)}
                />
                <PreviewRow
                  label="ต้นทุน/หน่วย"
                  before={formatAmount(holding.avgCost, holding.currency)}
                  after={formatAmount(next.avgCost, holding.currency)}
                  // ขายบางส่วนไม่กระทบต้นทุนเฉลี่ย (spec Section 5.4) — บอกไว้กันเข้าใจผิดว่าระบบลืมคิด
                  note={mode === 'sell' ? 'ขายแล้วต้นทุนเฉลี่ยไม่เปลี่ยน' : undefined}
                />
                <PreviewRow
                  label="ต้นทุนรวม"
                  before={formatAmount(totalCost(current), holding.currency)}
                  after={formatAmount(totalCost(next), holding.currency)}
                />
              </dl>
            )}

            {result.mode === 'sell' && <SaleRealizedRow sale={result.plan.sale} />}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-line-strong px-4 py-2 text-sm text-ink-300 transition hover:text-ink-100"
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            disabled={result === null || isSaving}
            className={[
              'rounded-md px-4 py-2 text-sm font-medium text-ink-100 transition disabled:opacity-50',
              isSellingOut ? 'bg-down hover:brightness-110' : 'bg-brand hover:brightness-110',
            ].join(' ')}
          >
            {isSaving ? 'กำลังบันทึก…' : isSellingOut ? 'ขายหมดและลบออกจากพอร์ต' : 'ยืนยัน'}
          </button>
        </div>
      </form>
    </div>
  );
}

function PreviewRow({
  label,
  before,
  after,
  note,
}: {
  label: string;
  before: string;
  after: string;
  note?: string;
}) {
  const changed = before !== after;

  /*
    ป้ายชื่ออยู่คนละบรรทัดกับตัวเลข ไม่ใช่ซ้าย-ขวาแบบเดิม

    ต้นทุนเฉลี่ยหลังถัวมีทศนิยมยาวได้ถึง 8 ตำแหน่ง พอเอามาต่อกันเป็น "ก่อน → หลัง"
    ในบรรทัดเดียวกับป้ายชื่อ บนจอ 375px มันยาวเกินจนโดนตัดทั้งสองฝั่ง อ่านไม่ได้เลย
    ให้ตัวเลขได้ความกว้างเต็มแล้วปล่อยให้ขึ้นบรรทัดใหม่เองถ้ายาวจริง ดีกว่าตัดทิ้ง
  */
  return (
    <div>
      <dt className="text-xs text-ink-400">{label}</dt>
      <dd className="mt-0.5 flex flex-wrap items-baseline gap-x-2 font-mono text-[13px]">
        <span className="text-ink-500 line-through">{before}</span>
        <span aria-hidden className="text-ink-600">
          →
        </span>
        <span className={changed ? 'text-ink-100' : 'text-ink-400'}>{after}</span>
      </dd>
      {note !== undefined && <p className="mt-0.5 text-[11px] text-ink-600">{note}</p>}
    </div>
  );
}
